import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Profile } from '../models';
import { useActiveRole } from './RoleContext';

/**
 * Auth boundary backed by Google OAuth (via /api/auth/me).
 *
 * Server-side cookie says yes/no — `canAdmin` reflects the server-checked
 * admin policy (ADMIN_EMAILS plus SUPER_ADMIN_EMAILS inheritance). The persona
 * switcher remains for picking which profile/role to "act as" in the UI;
 * admin access is independently gated.
 *
 * Local dev fallback: if /api/auth/me is unreachable (no functions running)
 * we fall back to the old "is staff role" check so the in-browser persona
 * picker still works for design/build flows.
 */
interface AuthContextValue {
  isSignedIn: boolean;
  /** True for anyone who can open League HQ. */
  canAdmin: boolean;
  /** True for the platform operator (you). Subset of canAdmin. */
  canSuperAdmin: boolean;
  /** Email from the Google session, if any. */
  googleEmail: string | null;
  /** Temporary public browsing mode for HQ QA. Does not mean a real user is signed in. */
  publicHqAccess: boolean;
  /** True once /api/auth/me has resolved (either way). */
  authReady: boolean;
  profile: Profile;
}

interface MeResponse {
  authenticated: boolean;
  email?: string;
  isAdmin?: boolean;
  isSuperAdmin?: boolean;
  publicHqAccess?: boolean;
  testingAccessOverride?: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const STAFF_ROLES = new Set(['admin', 'scorekeeper', 'referee']);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { profile } = useActiveRole();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [reachable, setReachable] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/auth/me', { credentials: 'same-origin' })
      .then(async (r) => {
        if (!r.ok) throw new Error(`status ${r.status}`);
        return (await r.json()) as MeResponse;
      })
      .then((data) => {
        if (cancelled) return;
        setMe(data);
        setReachable(true);
      })
      .catch(() => {
        if (cancelled) return;
        setReachable(false);
      })
      .finally(() => {
        if (!cancelled) setAuthReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const isStaff = profile.globalRoles.some((r) => STAFF_ROLES.has(r));

    // When the auth endpoint is reachable, it's the source of truth.
    if (reachable && me) {
      const publicHqAccess = !!me.publicHqAccess;
      return {
        isSignedIn: me.authenticated,
        canAdmin: !!me.isAdmin || publicHqAccess,
        canSuperAdmin: !!me.isSuperAdmin || publicHqAccess,
        googleEmail: me.email ?? null,
        publicHqAccess,
        authReady,
        profile,
      };
    }

    // Local dev / endpoint unreachable: persona-based fallback.
    return {
      isSignedIn: true,
      canAdmin: isStaff,
      canSuperAdmin: isStaff,
      googleEmail: null,
      publicHqAccess: false,
      authReady,
      profile,
    };
  }, [me, reachable, authReady, profile]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
